import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, ChevronRight, Loader2, MessageSquareText, Minus, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { withCsrfHeaders } from '../utils/csrf';
import { RAG_THINKING_LABEL } from './RagChrome';
import { getJaneUserId } from './ragIdentityUtils';
import { createRagClientSaveToken } from './ragOperationGuards';
import { fetchRagJsonWithTimeout } from './ragStreamUtils';
import '../styles/PersistentJaneDock.css';

const API_BASE = 'https://api.casedb.co';
const JANE_WORKSPACE_HANDOFF_STORAGE_KEY = 'jane-workspace-handoff';
const JANE_WORKSPACE_PLAN_STORAGE_KEY = 'jane-workspace-plan';
const JANE_DOCK_SELECTED_CHAT_STORAGE_KEY = 'persistent-jane-dock-selected-chat';
const JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY = 'persistent-jane-dock-prompt-draft';
const MAX_DOCK_MESSAGES = 10;
const JANE_DOCK_READ_TIMEOUT_MS = 12000;
const JANE_DOCK_ACTION_TIMEOUT_MS = 30000;

const getDockFailureMessage = (error, fallbackMessage) => (
    error?.isSafeForDisplay && String(error?.message || '').trim()
        ? String(error.message).trim()
        : fallbackMessage
);

const RagSidebarMark = ({ className = '' }) => (
    <svg className={className} width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true" focusable="false">
        <path d="M5.1 4.2C4.15 4.2 3.38 4.97 3.38 5.92V16.08C3.38 17.03 4.15 17.8 5.1 17.8H16.9C17.85 17.8 18.62 17.03 18.62 16.08V5.92C18.62 4.97 17.85 4.2 16.9 4.2H5.1Z" stroke="currentColor" strokeWidth="1.45" />
        <path d="M8.35 4.55V17.45" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" />
        <path d="M11.15 8.15H15.55" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
        <path d="M11.15 11H14.7" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
        <path d="M11.15 13.85H15.05" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
    </svg>
);

const parseWorkspaceSelectionOrdinal = (value = '') => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return null;
    const match = normalized.match(/^(?:case\s*)?(\d{1,2})\.?$/i);
    if (!match) return null;
    const ordinal = Number(match[1]);
    return Number.isFinite(ordinal) && ordinal > 0 ? ordinal : null;
};

const extractWorkspaceCaseCandidate = (value = '') => {
    const prompt = String(value || '').replace(/\s+/g, ' ').trim();
    if (!prompt) return '';

    const normalizeCandidate = (candidateValue = '') => {
        let candidate = String(candidateValue || '').trim().replace(/^[\s.:;!?—–-]+|[\s.:;!?—–-]+$/g, '');
        candidate = candidate.replace(/^(?:the|this|that)\s+/i, '');
        candidate = candidate.replace(/^(?:activity\s+feed|feed\s+entry|feed|draft|note|task)\s+(?:in|for|of)\s+/i, '');
        candidate = candidate.replace(/^(?:(?:expenses?|costs?|case\s+expenses?|documents?|files?|medical|billing)\s+)?section\s+(?:in|for|of|on|to)\s+/i, '');
        candidate = candidate.replace(/^(?:expenses?|costs?|case\s+expenses?)\s+(?:section\s+)?(?:in|for|of|on|to)\s+/i, '');
        candidate = candidate.replace(/^.+?\s+section\s+(?:in|for|of|on|to)\s+/i, '');
        candidate = candidate.replace(/^(?:the|this|that)\s+/i, '');
        candidate = candidate.replace(/\b(?:expenses?|costs?|case\s+expenses?|documents?|files?|medical|billing)\s+section\b/i, '');
        candidate = candidate.replace(/\b(?:case|matter|file|please)\b.*$/i, '').trim().replace(/^[\s.:;!?—–-]+|[\s.:;!?—–-]+$/g, '');
        return /^(?:current|active|opened|selected|this|that)$/i.test(candidate) ? '' : candidate;
    };

    const priorityPatterns = [
        /\b(?:take\s+me\s+to|show\s+me|open|go\s+to|navigate\s+to|bring\s+up)\s+(?:the\s+)?(?:expenses?|costs?|case\s+expenses?)\s+section\s+(?:in|for|of|on|to)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\b(?:expenses?|costs?|case\s+expenses?)\s+section\s+(?:in|for|of|on|to)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\b(?:take\s+me\s+to|show\s+me|open|go\s+to|navigate\s+to|bring\s+up)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\bactivity\s+feed\s+(?:in|for|of)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\b(?:draft|create|start)\b.*?\b(?:in|for|of)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
    ];
    for (const pattern of priorityPatterns) {
        const match = prompt.match(pattern);
        const candidate = normalizeCandidate(match?.[1] || '');
        if (candidate) return candidate;
    }

    const genericMatches = [...prompt.matchAll(/\b(?:in|for|of|on|to|regarding|about|re:)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/gi)];
    for (let index = genericMatches.length - 1; index >= 0; index -= 1) {
        const candidate = normalizeCandidate(genericMatches[index]?.[1] || '');
        if (candidate) return candidate;
    }

    const trailingMatch = prompt.match(/\b([A-Z][\p{L}\p{M}'’.-]+(?:\s+[A-Za-z][\p{L}\p{M}'’.-]+){1,5})\s+(?:case|matter)\b/u)
        || prompt.match(/\b([a-z][\p{L}\p{M}'’.-]+(?:\s+[a-z][\p{L}\p{M}'’.-]+){1,5})\s+(?:case|matter)\b/iu);
    return normalizeCandidate(trailingMatch?.[1] || '');
};

const normalizeWorkspaceCaseSelectionText = (value = '') => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\b(?:case|matter|please|open|take\s+me|show\s+me|go\s+to|navigate\s+to|expenses?|costs?|section|billing|documents?|files?)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findWorkspaceCaseSelectionByText = (value = '', caseMatches = []) => {
    const normalizedValue = normalizeWorkspaceCaseSelectionText(value);
    if (!normalizedValue || !Array.isArray(caseMatches)) return null;
    const scored = caseMatches
        .map((match) => {
            const caseName = String(match?.case_name || match?.name || '').trim();
            const normalizedName = normalizeWorkspaceCaseSelectionText(caseName);
            const caseId = Number(match?.case_id || match?.id || 0);
            let score = 0;
            if (normalizedName && normalizedName === normalizedValue) score += 100;
            if (normalizedName && normalizedName.includes(normalizedValue)) score += 70;
            if (normalizedName && normalizedValue.includes(normalizedName)) score += 60;
            const valueTokens = new Set(normalizedValue.split(' ').filter(token => token.length >= 3));
            const nameTokens = normalizedName.split(' ').filter(token => token.length >= 3);
            const matchedTokens = nameTokens.filter(token => valueTokens.has(token)).length;
            if (matchedTokens > 0) score += matchedTokens * 12;
            if (caseId > 0 && normalizedValue.includes(String(caseId))) score += 80;
            return { match, score };
        })
        .filter(item => item.score >= 24)
        .sort((left, right) => right.score - left.score);
    if (scored.length === 0) return null;
    if (scored.length > 1 && scored[0].score === scored[1].score) return null;
    return scored[0].match;
};

const normalizeMessageText = (message = {}) => {
    const answerText = String(message?.answer?.answer_text || '').trim();
    const contentText = String(message?.content || message?.query_text || '').trim();
    return answerText || contentText;
};

const normalizeChatMessages = (messages = []) => (Array.isArray(messages) ? messages : [])
    .map((message) => ({
        ...message,
        id: String(message?.id || `${message?.role || 'message'}-${message?.created_at || Math.random()}`),
        role: message?.role === 'user' ? 'user' : 'assistant',
        content: normalizeMessageText(message),
    }))
    .filter((message) => message.content || message.pending)
    .slice(-MAX_DOCK_MESSAGES);

const formatRelativeTimestamp = (value) => {
    if (!value) return '';
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return '';
    const diffMs = Date.now() - timestamp;
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes <= 0) return 'now';
    if (diffMinutes < 60) return `${diffMinutes}m`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays}d`;
};

const sortChatsByTime = (chats = []) => [...chats].sort((left, right) => {
    const leftTime = new Date(left?.last_message_at || left?.updated_at || left?.created_at || 0).getTime();
    const rightTime = new Date(right?.last_message_at || right?.updated_at || right?.created_at || 0).getTime();
    return rightTime - leftTime;
});

const buildChatsSignature = (chatList = []) => JSON.stringify((Array.isArray(chatList) ? chatList : []).map((chat) => ({
    id: Number(chat?.id || 0),
    title: String(chat?.title || ''),
    updated_at: chat?.last_message_at || chat?.updated_at || chat?.created_at || '',
    active_request: chat?.active_request?.request_token || chat?.active_request?.status || '',
})));

const buildMessagesSignature = (messageList = []) => JSON.stringify((Array.isArray(messageList) ? messageList : []).map((message) => ({
    id: String(message?.id || ''),
    role: String(message?.role || ''),
    content: String(message?.content || ''),
    created_at: message?.created_at || '',
    pending: Boolean(message?.pending),
})));

const buildActiveRequestSignature = (activeRequest = null) => activeRequest
    ? JSON.stringify({
        request_token: activeRequest.request_token || '',
        status: activeRequest.status || '',
        status_message: activeRequest.status_message || '',
        answer_preview: activeRequest.answer_preview || '',
        updated_at: activeRequest.updated_at || '',
    })
    : '';

const PersistentJaneDock = ({ user, loggedIn, hideLauncher = false }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(true);
    const [chatListCollapsed, setChatListCollapsed] = useState(false);
    const [dockLoading, setDockLoading] = useState(false);
    const [dockError, setDockError] = useState('');
    const [dockRetryNonce, setDockRetryNonce] = useState(0);
    const [chats, setChats] = useState([]);
    const [selectedChatId, setSelectedChatId] = useState(() => {
        if (typeof window === 'undefined') return null;
        const savedValue = window.sessionStorage.getItem(JANE_DOCK_SELECTED_CHAT_STORAGE_KEY);
        const parsedValue = Number(savedValue);
        return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
    });
    const [selectedMessages, setSelectedMessages] = useState([]);
    const [selectedChatLoading, setSelectedChatLoading] = useState(false);
    const [composerDraft, setComposerDraft] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedActiveRequest, setSelectedActiveRequest] = useState(null);
    const [pendingWorkspaceSelection, setPendingWorkspaceSelection] = useState(null);
    const [expandedMessageIds, setExpandedMessageIds] = useState(() => new Set());
    const messagesEndRef = useRef(null);
    const composerRef = useRef(null);
    const handoffInFlightRef = useRef(false);
    const composerFocusTimeoutRef = useRef(null);
    const threadRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const chatsSignatureRef = useRef('');
    const selectedMessagesSignatureRef = useRef('');
    const selectedActiveRequestSignatureRef = useRef('');
    const consumedHandoffKeyRef = useRef('');
    const chatsLoadSequenceRef = useRef(0);
    const selectedChatLoadSequenceRef = useRef(0);

    const userId = getJaneUserId(user);
    const hidden = !loggedIn || !user || userId === null || /^\/ai(?:\/|$)/.test(location.pathname || '');
    const dockOpen = !hidden && !collapsed;
    const sortedChats = useMemo(() => sortChatsByTime(chats), [chats]);
    const selectedChat = sortedChats.find((chat) => Number(chat.id) === Number(selectedChatId)) || null;
    const currentCaseId = useMemo(() => {
        const match = String(location.pathname || '').match(/^\/case\/(\d+)/);
        return match ? Number(match[1]) : 0;
    }, [location.pathname]);

    const persistSavedTurn = useCallback(async (chatId, query, answerText, extra = {}) => {
        const { data: saveData } = await fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({
                action: 'save_turn',
                chat_id: chatId,
                query,
                answer: {
                    answer_text: answerText,
                    citations: [],
                    source_descriptions: [],
                },
                files: extra.files || [],
                intelligence: extra.intelligence || null,
                client_save_token: String(extra.clientSaveToken || '').trim() || createRagClientSaveToken(),
                model_key: selectedChat?.model_key || 'auto',
                chat_scope: 'firm-wide',
            }),
        }, {
            timeoutMs: JANE_DOCK_ACTION_TIMEOUT_MS,
            failureMessage: 'Jane could not save that follow-up. Please try again.',
            timeoutMessage: 'Jane took too long to save that follow-up. Please try again.',
        });
        if (!saveData?.success) {
            throw new Error('Jane could not save that follow-up. Please try again.');
        }

        setChats((prev) => sortChatsByTime([saveData.chat, ...prev.filter((chat) => Number(chat.id) !== Number(saveData.chat.id))]));
        setSelectedChatId(Number(saveData.chat.id));
        setSelectedMessages(normalizeChatMessages(saveData.messages || []));
    }, [selectedChat?.model_key]);

    const clearWorkspaceHandoff = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(JANE_WORKSPACE_HANDOFF_STORAGE_KEY);
        }

        if (location.search.includes('jane_open=1') || location.search.includes('jane_handoff=')) {
            const nextParams = new URLSearchParams(location.search);
            nextParams.delete('jane_open');
            nextParams.delete('jane_handoff');
            const nextSearch = nextParams.toString();
            navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true, state: {} });
        }
    }, [location.pathname, location.search, navigate]);

    const storeWorkspaceHandoff = useCallback((caseId, prompt, actions = [], chatId = null) => {
        if (typeof window === 'undefined') return;
        window.sessionStorage.setItem(JANE_WORKSPACE_HANDOFF_STORAGE_KEY, JSON.stringify({
            caseId,
            prompt,
            actions: Array.isArray(actions) ? actions : [],
            chatId: Number(chatId || selectedChatId || 0) || null,
            createdAt: Date.now(),
        }));
    }, [selectedChatId]);

    const dispatchWorkspacePlan = useCallback((caseId, actions = []) => {
        const plan = { caseId, actions, createdAt: Date.now() };
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(JANE_WORKSPACE_PLAN_STORAGE_KEY, JSON.stringify(plan));
        }
        window.dispatchEvent(new CustomEvent('janeWorkspacePlan', { detail: plan }));
    }, []);

    useLayoutEffect(() => {
        if (!threadRef.current || !shouldAutoScrollRef.current) {
            return;
        }
        const thread = threadRef.current;
        thread.scrollTop = thread.scrollHeight;
        window.requestAnimationFrame(() => {
            if (threadRef.current && shouldAutoScrollRef.current) {
                threadRef.current.scrollTop = threadRef.current.scrollHeight;
            }
        });
    }, [selectedChatId, selectedMessages]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (selectedChatId && Number(selectedChatId) > 0) {
            window.sessionStorage.setItem(JANE_DOCK_SELECTED_CHAT_STORAGE_KEY, String(selectedChatId));
            return;
        }
        window.sessionStorage.removeItem(JANE_DOCK_SELECTED_CHAT_STORAGE_KEY);
    }, [selectedChatId]);

    useEffect(() => () => {
        if (composerFocusTimeoutRef.current) {
            window.clearTimeout(composerFocusTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        const handleOpenDock = (event) => {
            setCollapsed(false);
            if (event.detail?.focusComposer) {
                if (composerFocusTimeoutRef.current) {
                    window.clearTimeout(composerFocusTimeoutRef.current);
                }
                composerFocusTimeoutRef.current = window.setTimeout(() => composerRef.current?.focus(), 0);
            }
        };

        window.addEventListener('openPersistentJaneDock', handleOpenDock);
        return () => window.removeEventListener('openPersistentJaneDock', handleOpenDock);
    }, []);

    useEffect(() => {
        if (!dockOpen) return undefined;

        let cancelled = false;
        let requestInFlight = false;
        const loadChats = async (showLoading = false) => {
            if (requestInFlight) return;
            requestInFlight = true;
            const loadId = ++chatsLoadSequenceRef.current;
            if (showLoading) setDockLoading(true);
            try {
                const { data } = await fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php?action=dock&limit=8&messages_limit=4`, {
                    credentials: 'include',
                }, {
                    timeoutMs: JANE_DOCK_READ_TIMEOUT_MS,
                    failureMessage: 'Jane activity could not be loaded. Please try again.',
                    timeoutMessage: 'Jane activity took too long to load. Please try again.',
                });
                if (cancelled || loadId !== chatsLoadSequenceRef.current) return;
                if (!data?.success) {
                    setDockError('Jane activity could not be loaded. Please try again.');
                    return;
                }

                const nextChats = sortChatsByTime(Array.isArray(data.chats) ? data.chats : []);
                const nextChatsSignature = buildChatsSignature(nextChats);
                if (nextChatsSignature !== chatsSignatureRef.current) {
                    chatsSignatureRef.current = nextChatsSignature;
                    setChats(nextChats);
                }
                setDockError('');

                setSelectedChatId((currentId) => {
                    if (nextChats.some((chat) => Number(chat.id) === Number(currentId))) {
                        return currentId;
                    }
                    const serverSelectedId = Number(data?.selected_chat_id || data?.selected_chat?.id || nextChats[0]?.id || 0);
                    if (Number.isFinite(serverSelectedId) && serverSelectedId > 0) {
                        return serverSelectedId;
                    }
                    return null;
                });

            } catch (error) {
                if (!cancelled) {
                    setDockError(getDockFailureMessage(error, 'Jane activity could not be loaded. Please try again.'));
                }
            } finally {
                requestInFlight = false;
                if (!cancelled && loadId === chatsLoadSequenceRef.current) setDockLoading(false);
            }
        };

        loadChats(true);
        const intervalId = window.setInterval(() => loadChats(false), 12000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [dockOpen, dockRetryNonce]);

    useEffect(() => {
        if (hidden || !selectedChatId) {
            setSelectedMessages([]);
            setSelectedActiveRequest(null);
            return undefined;
        }

        if (!dockOpen) {
            return undefined;
        }

        let cancelled = false;
        let previousSelectedChatId = selectedChatId;
        let requestInFlight = false;
        const loadSelectedChat = async (showLoading = false) => {
            if (requestInFlight) return;
            requestInFlight = true;
            const loadId = ++selectedChatLoadSequenceRef.current;
            if (showLoading) setSelectedChatLoading(true);
            try {
                const chatParams = new URLSearchParams({
                    action: 'get',
                    chat_id: String(selectedChatId),
                    messages_limit: '40',
                });
                const [chatResult, statusResult] = await Promise.allSettled([
                    fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php?${chatParams.toString()}`, { credentials: 'include' }, {
                        timeoutMs: JANE_DOCK_READ_TIMEOUT_MS,
                        failureMessage: 'The selected Jane chat could not be loaded. Please try again.',
                        timeoutMessage: 'The selected Jane chat took too long to load. Please try again.',
                    }),
                    fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php?action=status&chat_id=${selectedChatId}`, { credentials: 'include' }, {
                        timeoutMs: JANE_DOCK_READ_TIMEOUT_MS,
                        failureMessage: 'Jane could not refresh the current response status.',
                        timeoutMessage: 'Jane took too long to refresh the current response status.',
                    }),
                ]);
                if (chatResult.status === 'rejected') throw chatResult.reason;
                const chatData = chatResult.value.data;
                const statusData = statusResult.status === 'fulfilled' ? statusResult.value.data : null;
                if (cancelled || loadId !== selectedChatLoadSequenceRef.current) return;
                if (!chatData?.success) {
                    setDockError('The selected Jane chat could not be loaded. Please try again.');
                    return;
                }
                const nextMessages = normalizeChatMessages(chatData.messages || []);
                const nextMessagesSignature = buildMessagesSignature(nextMessages);
                if (nextMessagesSignature !== selectedMessagesSignatureRef.current) {
                    selectedMessagesSignatureRef.current = nextMessagesSignature;
                    setSelectedMessages(nextMessages);
                }

                if (Number(previousSelectedChatId) !== Number(selectedChatId)) {
                    setExpandedMessageIds(new Set());
                    previousSelectedChatId = selectedChatId;
                }

                const nextActiveRequest = statusData?.success ? (statusData.active_request || null) : null;
                const nextActiveRequestSignature = buildActiveRequestSignature(nextActiveRequest);
                if (nextActiveRequestSignature !== selectedActiveRequestSignatureRef.current) {
                    selectedActiveRequestSignatureRef.current = nextActiveRequestSignature;
                    setSelectedActiveRequest(nextActiveRequest);
                }
                setDockError('');
                shouldAutoScrollRef.current = true;
            } catch (error) {
                if (!cancelled) {
                    setDockError(getDockFailureMessage(error, 'The selected Jane chat could not be loaded. Please try again.'));
                }
            } finally {
                requestInFlight = false;
                if (!cancelled && loadId === selectedChatLoadSequenceRef.current) setSelectedChatLoading(false);
            }
        };

        selectedMessagesSignatureRef.current = '';
        selectedActiveRequestSignatureRef.current = '';
        loadSelectedChat(true);
        const intervalId = window.setInterval(() => loadSelectedChat(false), 6000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [dockOpen, dockRetryNonce, hidden, selectedChatId]);

    const handleOpenFullJane = () => {
        if (selectedChat?.id) {
            navigate(`/ai?chat_id=${selectedChat.id}`);
            return;
        }
        navigate('/ai');
    };

    const ensureSelectedChat = useCallback(async (preferredChatId = null) => {
        if (userId === null) throw new Error('Your CaseDB session could not be verified. Refresh the page and try again.');
        const parsedPreferredChatId = Number(preferredChatId || 0);
        if (Number.isFinite(parsedPreferredChatId) && parsedPreferredChatId > 0) {
            setSelectedChatId(parsedPreferredChatId);
            return parsedPreferredChatId;
        }
        if (selectedChat?.id) return selectedChat.id;
        const { data } = await fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({ action: 'create', title: 'New Jane chat', chat_scope: 'firm-wide', model_key: 'auto' }),
        }, {
            timeoutMs: JANE_DOCK_ACTION_TIMEOUT_MS,
            failureMessage: 'Jane could not start a new chat. Please try again.',
            timeoutMessage: 'Jane took too long to start a new chat. Please try again.',
        });
        if (!data?.success || !data?.chat?.id) {
            throw new Error('Jane could not start a new chat. Please try again.');
        }
        setChats((prev) => sortChatsByTime([data.chat, ...prev.filter((chat) => Number(chat.id) !== Number(data.chat.id))]));
        setSelectedChatId(Number(data.chat.id));
        shouldAutoScrollRef.current = true;
        return Number(data.chat.id);
    }, [selectedChat, userId]);

    const submitJanePrompt = useCallback(async (trimmedDraft, options = {}) => {
        if (userId === null) throw new Error('Your CaseDB session could not be verified. Refresh the page and try again.');
        const chatId = await ensureSelectedChat(options.chatIdOverride || null);
        const workspaceSelectionOrdinal = !options.force ? parseWorkspaceSelectionOrdinal(trimmedDraft) : null;
        const explicitCaseCandidate = extractWorkspaceCaseCandidate(trimmedDraft);

        const confirmedWorkspaceCaseSelection = Boolean(options.confirmedCaseId || Number(options.caseIdOverride || 0) > 0);
        if (!confirmedWorkspaceCaseSelection && (workspaceSelectionOrdinal !== null || pendingWorkspaceSelection?.caseMatches?.length) && pendingWorkspaceSelection?.caseMatches?.length) {
            const selectedCase = workspaceSelectionOrdinal !== null
                ? (pendingWorkspaceSelection.caseMatches[workspaceSelectionOrdinal - 1] || null)
                : findWorkspaceCaseSelectionByText(trimmedDraft, pendingWorkspaceSelection.caseMatches);
            if (selectedCase && Number(selectedCase.case_id || selectedCase.id) > 0) {
                const workspaceCaseId = Number(selectedCase.case_id || selectedCase.id);
                const { data: planData } = await fetchRagJsonWithTimeout(`${API_BASE}/jane-workspace.php`, {
                    method: 'POST',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    credentials: 'include',
                    body: JSON.stringify({
                        case_id: workspaceCaseId,
                        confirmed_case_id: true,
                        prompt: pendingWorkspaceSelection.originalPrompt,
                        current_path: `${window.location.pathname}${window.location.search}`,
                    }),
                }, {
                    timeoutMs: JANE_DOCK_ACTION_TIMEOUT_MS,
                    failureMessage: 'Jane could not prepare that case action. Please try again.',
                    timeoutMessage: 'Jane took too long to prepare that case action. Please try again.',
                });
                if (!planData?.success || planData.mode !== 'workspace_navigation') {
                    throw new Error('Jane could not prepare that case action. Please try again.');
                }
                const workspaceActions = Array.isArray(planData.actions) ? planData.actions : [];
                const answerText = String(planData.summary || `Opening ${String(selectedCase.case_name || selectedCase.name || `Case ${workspaceCaseId}`).trim()}…`).trim();
                await persistSavedTurn(
                    chatId,
                    trimmedDraft,
                    answerText,
                    { intelligence: { ...planData, selected_case_id: workspaceCaseId } }
                );
                storeWorkspaceHandoff(workspaceCaseId, pendingWorkspaceSelection.originalPrompt, workspaceActions, chatId);
                setPendingWorkspaceSelection(null);
                setCollapsed(false);
                navigate(`/case/${workspaceCaseId}?jane_open=1&jane_handoff=workspace`, {
                    state: { janeWorkspacePrompt: pendingWorkspaceSelection.originalPrompt, janeWorkspaceCaseId: workspaceCaseId },
                });
                return;
            }
        }
        const { data: workspaceData } = await fetchRagJsonWithTimeout(`${API_BASE}/jane-workspace.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({
                case_id: !explicitCaseCandidate && currentCaseId > 0 ? currentCaseId : undefined,
                explicit_case_candidate: explicitCaseCandidate || undefined,
                prompt: trimmedDraft,
                current_path: `${window.location.pathname}${window.location.search}`,
            }),
        }, {
            timeoutMs: JANE_DOCK_ACTION_TIMEOUT_MS,
            failureMessage: 'Jane could not check that workspace request. Please try again.',
            timeoutMessage: 'Jane took too long to check that workspace request. Please try again.',
        });

        if (workspaceData?.success) {
            if (workspaceData.mode === 'case_selection_required') {
                const caseMatches = Array.isArray(workspaceData.case_matches) ? workspaceData.case_matches : [];
                const answerText = caseMatches.length > 0
                    ? `${String(workspaceData.message || 'Case context is required for this guided workspace action.').trim()}\n\n${caseMatches.slice(0, 4).map((match, index) => `${index + 1}. ${String(match?.case_name || match?.name || `Case ${match?.case_id || ''}`).trim()}`).join('\n')}`
                    : String(workspaceData.message || 'Case context is required for this guided workspace action.').trim();
                await persistSavedTurn(chatId, trimmedDraft, answerText, { intelligence: workspaceData });
                setPendingWorkspaceSelection({ originalPrompt: trimmedDraft, caseMatches, createdAt: Date.now() });
                shouldAutoScrollRef.current = true;
                return;
            }

            if (workspaceData.mode === 'contact_selection_required') {
                const answerText = String(workspaceData.message || 'I need you to confirm which contact to use.').trim();
                await persistSavedTurn(chatId, trimmedDraft, answerText, { intelligence: workspaceData });
                shouldAutoScrollRef.current = true;
                return;
            }

            if (workspaceData.mode === 'workspace_navigation' && Number(workspaceData?.session?.case_id) > 0) {
                const workspaceCaseId = Number(workspaceData.session.case_id);
                const answerText = String(workspaceData.answer?.answer_text || workspaceData.summary || 'I can open the requested workspace section.').trim() || 'I can open the requested workspace section.';
                await persistSavedTurn(chatId, trimmedDraft, answerText, { intelligence: workspaceData });
                setPendingWorkspaceSelection(null);
                shouldAutoScrollRef.current = true;

                if (workspaceCaseId === currentCaseId && currentCaseId > 0) {
                    dispatchWorkspacePlan(workspaceCaseId, Array.isArray(workspaceData.actions) ? workspaceData.actions : []);
                    clearWorkspaceHandoff();
                    return;
                }

                storeWorkspaceHandoff(workspaceCaseId, trimmedDraft, Array.isArray(workspaceData.actions) ? workspaceData.actions : [], chatId);
                navigate(`/case/${workspaceCaseId}?jane_open=1&jane_handoff=workspace`, {
                    state: { janeWorkspacePrompt: trimmedDraft, janeWorkspaceCaseId: workspaceCaseId },
                });
                return;
            }
        }

        const { data: responseData } = await fetchRagJsonWithTimeout(`${API_BASE}/library-intelligence-search.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({
                action: 'run',
                mode: 'firm-wide',
                q: trimmedDraft,
                page: '1',
                page_size: '10',
                use_hybrid: '1',
                use_vector: '1',
                chat_id: String(chatId),
                model_key: selectedChat?.model_key || 'auto',
            }),
        }, {
            timeoutMs: JANE_DOCK_ACTION_TIMEOUT_MS,
            failureMessage: 'Jane could not send that follow-up. Please try again.',
            timeoutMessage: 'Jane took too long to answer that follow-up. Please try again.',
        });
        if (!responseData?.success) {
            throw new Error('Jane could not send that follow-up. Please try again.');
        }

        await persistSavedTurn(
            chatId,
            trimmedDraft,
            responseData?.intelligence?.answer?.answer_text || responseData?.answer?.answer_text || '',
            {
                files: responseData?.files || [],
                intelligence: responseData?.intelligence || null,
            }
        );
    }, [clearWorkspaceHandoff, currentCaseId, dispatchWorkspacePlan, ensureSelectedChat, navigate, pendingWorkspaceSelection, persistSavedTurn, selectedChat?.model_key, storeWorkspaceHandoff, userId]);

    useEffect(() => {
        if (hidden || currentCaseId <= 0 || handoffInFlightRef.current) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const shouldConsumeHandoff = params.get('jane_open') === '1' || params.get('jane_handoff') === 'workspace';
        if (!shouldConsumeHandoff || typeof window === 'undefined') {
            return;
        }

        const raw = window.sessionStorage.getItem(JANE_WORKSPACE_HANDOFF_STORAGE_KEY);
        if (!raw) {
            clearWorkspaceHandoff();
            return;
        }

        try {
            const parsed = JSON.parse(raw);
            if (Number(parsed?.caseId) !== currentCaseId || !String(parsed?.prompt || '').trim()) {
                clearWorkspaceHandoff();
                return;
            }

            const handoffKey = `${parsed.caseId}:${parsed.createdAt || ''}:${parsed.prompt}`;
            if (consumedHandoffKeyRef.current === handoffKey) {
                return;
            }
            consumedHandoffKeyRef.current = handoffKey;

            if (Array.isArray(parsed?.actions) && parsed.actions.length > 0) {
                setCollapsed(false);
                clearWorkspaceHandoff();
                dispatchWorkspacePlan(currentCaseId, parsed.actions);
                return;
            }

            if (String(parsed?.source || '').trim() === 'ai_route') {
                clearWorkspaceHandoff();
                return;
            }

            handoffInFlightRef.current = true;
            setCollapsed(false);
            setDockError('');
            clearWorkspaceHandoff();

            submitJanePrompt(String(parsed.prompt).trim(), { auto: true, chatIdOverride: parsed.chatId || null })
                .catch((error) => {
                    setDockError(getDockFailureMessage(error, 'Jane could not open that case workspace. Please try again.'));
                })
                .finally(() => {
                    handoffInFlightRef.current = false;
                });
        } catch (error) {
            clearWorkspaceHandoff();
        }
    }, [clearWorkspaceHandoff, currentCaseId, dispatchWorkspacePlan, hidden, location.search, submitJanePrompt]);

    const handleSubmitFollowUp = async (event) => {
        event.preventDefault();
        const trimmedDraft = composerDraft.trim();
        if (trimmedDraft.length < 2 || submitting) return;

        if (location.pathname !== '/ai') {
            const currentChatId = Number(selectedChat?.id || selectedChatId || 0) || null;
            if (typeof window !== 'undefined') {
                window.sessionStorage.setItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY, JSON.stringify({
                    chatId: currentChatId,
                    draft: trimmedDraft,
                    createdAt: Date.now(),
                }));
            }

            navigate(currentChatId ? `/ai?chat_id=${currentChatId}` : '/ai', {
                state: { janeDockPromptDraft: trimmedDraft, janeDockPromptChatId: currentChatId },
            });
            setComposerDraft('');
            return;
        }

        setSubmitting(true);
        setDockError('');
        const optimisticUserId = `local-dock-user-${Date.now()}`;
        const optimisticAssistantId = `local-dock-assistant-${Date.now()}`;
        setSelectedMessages(prev => [
            ...prev,
            {
                id: optimisticUserId,
                role: 'user',
                content: trimmedDraft,
                pending: false,
                created_at: new Date().toISOString(),
            },
            {
                id: optimisticAssistantId,
                role: 'assistant',
                content: RAG_THINKING_LABEL,
                pending: true,
                created_at: new Date().toISOString(),
            },
        ].slice(-MAX_DOCK_MESSAGES));
        try {
            shouldAutoScrollRef.current = true;
            await submitJanePrompt(trimmedDraft);

            setComposerDraft('');
        } catch (error) {
            setSelectedMessages(prev => prev.filter(message => message.id !== optimisticAssistantId && message.id !== optimisticUserId));
            setDockError(getDockFailureMessage(error, 'Jane could not send that follow-up. Your message is still in the box so you can try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    if (hidden) {
        return null;
    }

    const toggleMessageExpanded = (messageId) => {
        setExpandedMessageIds((prev) => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    };

    const handleComposerKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
        }
    };

    const handleThreadScroll = () => {
        const thread = threadRef.current;
        if (!thread) return;
        const distanceToBottom = thread.scrollHeight - thread.scrollTop - thread.clientHeight;
        shouldAutoScrollRef.current = distanceToBottom < 24;
    };

    const handleRetryDock = () => {
        setDockError('');
        setDockRetryNonce((value) => value + 1);
    };

    if (collapsed && hideLauncher) {
        return null;
    }

    if (collapsed) {
        return (
            <button
                type="button"
                className="action alt persistent-jane-launcher"
                onClick={() => setCollapsed(false)}
                aria-label="Open Jane recent activity"
            >
                <Sparkles size={20} className="persistent-jane-launcher-icon" />
                <span className="persistent-jane-launcher-label">Jane</span>
            </button>
        );
    }

    return (
        <aside className={`persistent-jane-dock${chatListCollapsed ? ' chatlist-collapsed' : ''}`} aria-label="Jane recent activity">
            <div className="persistent-jane-dock-header">
                <div>
                    <div className="persistent-jane-dock-eyebrow">Jane</div>
                    <div className="persistent-jane-dock-title">Recent chats</div>
                </div>
                <div className="persistent-jane-dock-header-actions">
                    <button
                        type="button"
                        className="persistent-jane-dock-sidebar-toggle"
                        onClick={() => setChatListCollapsed((prev) => !prev)}
                        aria-label={chatListCollapsed ? 'Expand Jane chat list' : 'Collapse Jane chat list'}
                        aria-expanded={!chatListCollapsed}
                        title={chatListCollapsed ? 'Show chats' : 'Hide chats'}
                    >
                        <RagSidebarMark className="persistent-jane-dock-sidebar-icon" />
                    </button>
                    <button
                        type="button"
                        className="persistent-jane-dock-minimize"
                        onClick={() => setCollapsed(true)}
                        aria-label="Collapse Jane recent activity"
                    >
                        <Minus size={16} />
                    </button>
                    <button type="button" className="persistent-jane-dock-open" onClick={handleOpenFullJane}>
                        <ArrowUpRight size={16} />
                        <span>Open</span>
                    </button>
                </div>
            </div>

            <div className="persistent-jane-dock-layout">
                <div className="persistent-jane-dock-chatlist" aria-label="Jane chats">
                    {dockLoading && sortedChats.length === 0 ? (
                        <div className="persistent-jane-dock-empty"><Loader2 size={15} className="spinner" /><span>Loading chats…</span></div>
                    ) : sortedChats.length === 0 ? (
                        <div className="persistent-jane-dock-empty"><MessageSquareText size={15} /><span>No Jane chats yet.</span></div>
                    ) : (
                        sortedChats.map((chat) => (
                            <button
                                key={chat.id}
                                type="button"
                                className={`persistent-jane-dock-chatitem${Number(chat.id) === Number(selectedChatId) ? ' active' : ''}`}
                                onClick={() => {
                                    shouldAutoScrollRef.current = true;
                                    setSelectedChatId(Number(chat.id));
                                }}
                            >
                                <span className="persistent-jane-dock-chatitem-title">{chat.title || 'Untitled chat'}</span>
                                <span className="persistent-jane-dock-chatitem-meta">{formatRelativeTimestamp(chat.last_message_at || chat.updated_at || chat.created_at)}</span>
                            </button>
                        ))
                    )}
                </div>

                <div className="persistent-jane-dock-panel">
                    {selectedChat && (
                        <div className="persistent-jane-dock-chatmeta">
                            <span className="persistent-jane-dock-chatname">{selectedChat.title || 'Latest chat'}</span>
                            {selectedActiveRequest && (
                                <span className="persistent-jane-dock-badge">
                                    <Sparkles size={12} />
                                    <span>Streaming</span>
                                </span>
                            )}
                        </div>
                    )}

                    <div ref={threadRef} className="persistent-jane-dock-thread" onScroll={handleThreadScroll}>
                        {selectedChatLoading && selectedMessages.length === 0 ? (
                            <div className="persistent-jane-dock-empty"><Loader2 size={15} className="spinner" /><span>Loading chat…</span></div>
                        ) : selectedMessages.length === 0 ? (
                            <div className="persistent-jane-dock-empty"><MessageSquareText size={15} /><span>Select a chat or start a follow-up.</span></div>
                        ) : (
                            selectedMessages.map((message) => {
                                const isExpanded = expandedMessageIds.has(message.id);
                                const isExpandable = String(message.content || '').trim().length > 180;
                                return (
                                <div key={message.id} className={`persistent-jane-dock-message ${message.role}${message.pending ? ' pending' : ''}${isExpanded ? ' expanded' : ''}`}>
                                    <span className="persistent-jane-dock-message-role">{message.role === 'user' ? 'You' : 'Jane'}</span>
                                    <div className="persistent-jane-dock-message-body">
                                        <span className="persistent-jane-dock-message-text">{message.content}</span>
                                        {isExpandable && (
                                            <button
                                                type="button"
                                                className="persistent-jane-dock-message-toggle"
                                                onClick={() => toggleMessageExpanded(message.id)}
                                            >
                                                {isExpanded ? 'Collapse' : 'Expand'}
                                            </button>
                                        )}
                                    </div>
                                    <span className="persistent-jane-dock-message-time">{formatRelativeTimestamp(message.created_at)}</span>
                                </div>
                            )})
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="persistent-jane-dock-composer" onSubmit={handleSubmitFollowUp}>
                        <textarea
                            ref={composerRef}
                            value={composerDraft}
                            onChange={(event) => setComposerDraft(event.target.value)}
                            onKeyDown={handleComposerKeyDown}
                            placeholder={selectedChat ? 'Send a follow-up to this chat…' : 'Start a new Jane chat…'}
                            rows={2}
                        />
                        <div className="persistent-jane-dock-composer-actions">
                            {dockError ? (
                                <span className="persistent-jane-dock-error" role="alert">
                                    <span>{dockError}</span>
                                    <button type="button" className="persistent-jane-dock-retry" onClick={handleRetryDock}>Try again</button>
                                </span>
                            ) : <span className="persistent-jane-dock-hint">Accessible chats only</span>}
                            <button type="submit" className="action alt" disabled={composerDraft.trim().length < 2 || submitting}>
                                {submitting ? <Loader2 size={14} className="spinner" /> : <ChevronRight size={14} />}
                                <span>{selectedChat ? 'Send' : 'Start'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </aside>
    );
};

export default PersistentJaneDock;
